import React, { useContext, useEffect, useMemo } from 'react';
import { useField, useForm } from 'react-final-form';
import { useIntl } from 'react-intl';

import StarsDropDown from '@swf/components/Inputs/StarsDropDown';
import useWizardContext from '@swf/core/hooks/useWizardContext';

import VideoChatToggle from '../../../Modal/VideoChatToggle/VideoChatToggle';
import useForceUpdate from '../../../Modal/hooks/useForceUpdate';
import Container from '../../Container/Container';
import CreateTournamentContext from '../../CreateTournamentContext/CreateTournamentContext';
import useGetGameOptions from '../../hooks/useGetGameOptions';
import useSaveFormFields from '../../hooks/useSaveFormFields';
import canEnableVideo from '../../utils/canEnableVideo';
import type { FormValues, TournamentWizardContext } from '../../utils/common';
import { ModalPages } from '../../utils/common';
import getOptionsByCurrency from '../../utils/getOptionsByCurrency';
import getOptionsByHandType from '../../utils/getOptionsByHandType';
import getStandaloneOptions from '../../utils/getStandaloneOptions';

import {
  TXTCLI_Buy_In,
  TXTCLI_Currency,
  TXTCLI_Players_Per_Table,
  TXTCLI_Starting_Chips,
  TXTSRV_Game,
  TXTWCL_Type_of_Game
} from './GameDetailsForm.messages.json';
import styles from './GameDetailsForm.scss';

const GameDetailsForm: React.FC = () => {
  const { setState } = useContext(CreateTournamentContext);
  const { getState, change } = useForm();
  const { formatNumber } = useIntl();
  const { values: formValues } = getState();
  const {
    data: {
      tournamentStructuresResponse: { data },
      loading
    }
  } = useWizardContext<TournamentWizardContext, FormValues>();

  const { forceFieldUpdate } = useForceUpdate({
    values: formValues,
    change
  });

  const { handTypeOptions } = useGetGameOptions(data);

  const { startingChipsOptions } = useMemo(() => getStandaloneOptions(data), [data]);

  const {
    input: { value: handTypeValue }
  } = useField('handType', {
    subscription: { value: true },
    initialValue: handTypeOptions[0]?.value
  });

  const {
    input: { value: enableVideoChat }
  } = useField('enableVideoChat', {
    subscription: { value: true },
    initialValue: false
  });

  const { tableSizeOptions, potTypeOptions, currencyOptions } = useMemo(() => {
    const { handType, isHiLo } = JSON.parse(handTypeValue);
    return getOptionsByHandType(data, handType, isHiLo);
  }, [data, handTypeValue]);

  const {
    input: { value: currency }
  } = useField('currency', {
    subscription: { value: true },
    initialValue: currencyOptions[0]?.value
  });

  const buyInOptions = useMemo(
    () => getOptionsByCurrency(data, currency, formatNumber),
    [data, currency, formatNumber]
  );

  const {
    input: { value: tableSize }
  } = useField('tableSize', { initialValue: tableSizeOptions[0]?.value });

  const {
    input: { value: potStruct }
  } = useField('potStruct', { initialValue: potTypeOptions[0]?.value });

  const canEnableVideoOption = useMemo(() => {
    const { handType, isHiLo } = JSON.parse(handTypeValue);

    return canEnableVideo(data, handType, isHiLo, potStruct, tableSize);
  }, [data, handTypeValue, tableSize, potStruct]);

  useField('startingChips', { initialValue: startingChipsOptions[0]?.value });
  useField('buyIn', { initialValue: buyInOptions[0]?.value });

  useEffect(() => {
    const { handType, isHiLo } = JSON.parse(handTypeValue);
    const { tableSizeOptions, potTypeOptions, currencyOptions } = getOptionsByHandType(
      data,
      handType,
      isHiLo
    );

    forceFieldUpdate({ field: 'potStruct', options: potTypeOptions });
    forceFieldUpdate({ field: 'tableSize', options: tableSizeOptions });
    forceFieldUpdate({ field: 'currency', options: currencyOptions });
  }, [data, handTypeValue, forceFieldUpdate]);

  useEffect(() => {
    const buyInOptions = getOptionsByCurrency(data, currency, formatNumber);

    forceFieldUpdate({ field: 'buyIn', options: buyInOptions });
  }, [data, currency, forceFieldUpdate, formatNumber]);

  useEffect(
    () => setState((previousState) => ({ ...previousState, activePage: ModalPages.GameDetails })),
    [setState]
  );
  useSaveFormFields();

  useEffect(() => {
    if (!canEnableVideoOption && enableVideoChat) {
      change('enableVideoChat', false);
    }
  }, [canEnableVideoOption, enableVideoChat, change]);

  const handleChange = () => {
    change('enableVideoChat', !enableVideoChat);
  };

  return (
    <Container>
      <div className={styles.gameDetails}>
        <div>
          <StarsDropDown
            id="handType"
            label={TXTSRV_Game}
            options={handTypeOptions}
            className={styles.dropdown}
            disabled={loading}
          />
        </div>
        <div>
          <StarsDropDown
            id="potStruct"
            label={TXTWCL_Type_of_Game}
            options={potTypeOptions}
            className={styles.dropdown}
            disabled={loading}
          />
        </div>
        <VideoChatToggle
          checked={enableVideoChat}
          supported={canEnableVideoOption}
          onChange={handleChange}
          className={styles.videoChatToggle}
        />
        <div>
          <StarsDropDown
            id="tableSize"
            label={TXTCLI_Players_Per_Table}
            options={tableSizeOptions}
            className={styles.dropdown}
            disabled={loading}
          />
        </div>
        <div>
          <StarsDropDown
            id="currency"
            label={TXTCLI_Currency}
            options={currencyOptions}
            className={styles.dropdown}
            disabled={loading}
          />
        </div>
        <div>
          <StarsDropDown
            id="buyIn"
            label={TXTCLI_Buy_In}
            options={buyInOptions}
            className={styles.dropdown}
            disabled={loading}
          />
        </div>
        <div>
          <StarsDropDown
            id="startingChips"
            label={TXTCLI_Starting_Chips}
            options={startingChipsOptions}
            className={styles.dropdown}
            disabled={loading}
          />
        </div>
      </div>
    </Container>
  );
};

export default GameDetailsForm;
